/**
 * Last project the user opened.
 *
 * The source of truth is `users.last_project_id` on the server (so it follows
 * the user between devices — `me.get` already returns it as `me.project`).
 * This module keeps a per-user copy in localStorage + memory so that
 *
 *  - the app root can redirect to a project visited *after* `me` was fetched
 *    (the `me` payload is loaded once per page load, before any navigation), and
 *  - pages outside the project scope (`/settings`) keep showing the project
 *    the user came from instead of the server default.
 *
 * Whatever is stored is only a hint: callers validate it against the user's
 * current memberships before navigating (see `resolveLandingProjectId`).
 *
 * All storage access is wrapped in try/catch — SSR, private-mode and quota
 * errors never bubble out.
 */

import { useSyncExternalStore } from 'react'

const KEY_PREFIX = 'hub:lastProject:v1:'
const CHANGE_EVENT = 'hub:last-project-changed'

function storageKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`
}

export function readLastProjectId(userId: string | null | undefined): string | null {
  if (!userId) return null
  try {
    return window.localStorage.getItem(storageKey(userId))
  } catch {
    return null
  }
}

export function writeLastProjectId(userId: string, projectId: string): void {
  try {
    if (window.localStorage.getItem(storageKey(userId)) === projectId) return
    window.localStorage.setItem(storageKey(userId), projectId)
  } catch {
    // ignore — the server copy is the real one
  }
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/**
 * Drop the hint when it points at `projectId` — called when that project fails
 * to load (deleted, access revoked) so the app root does not send the user
 * straight back to the error page.
 */
export function forgetLastProjectId(userId: string, projectId: string): void {
  try {
    if (window.localStorage.getItem(storageKey(userId)) !== projectId) return
    window.localStorage.removeItem(storageKey(userId))
  } catch {
    return
  }
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // ignore
  }
}

/** Subscribe helper for `useSyncExternalStore`. */
export function subscribeLastProject(onChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

interface LandingProjectInput {
  /** `me.project?.id` — the server's pick (last visited, validated, else first). */
  serverProjectId: string | null | undefined
  /** `me.memberships.projects` ids — projects the user may open right now. */
  memberProjectIds: ReadonlyArray<string>
  /** Locally remembered id (may be stale: deleted project, revoked access). */
  storedProjectId: string | null | undefined
}

/**
 * Which project the app root should open. The local hint wins only when the
 * user still has a membership for it; otherwise the server's choice is used
 * (which itself falls back to the first project). Returns null when the user
 * has no project at all.
 */
export function resolveLandingProjectId({
  serverProjectId,
  memberProjectIds,
  storedProjectId,
}: LandingProjectInput): string | null {
  if (storedProjectId && memberProjectIds.includes(storedProjectId)) {
    return storedProjectId
  }
  return serverProjectId ?? null
}

/** Reactive view of the locally remembered project for `userId`. */
export function useLastProjectId(userId: string | null | undefined): string | null {
  return useSyncExternalStore(
    subscribeLastProject,
    () => readLastProjectId(userId),
    () => null
  )
}
